import { useColorMode } from '@chakra-ui/react';
import Image from 'next/image';
import Link from 'next/link';

type TutorialHeroProps = {
  quickstart: string;
  beforeYouStart: { title: string; link: string }[];
  exampleRepo?: { title: string; link: string }[];
  children: React.ReactNode;
};

/* The NextJS Quickstart is the only quickstart to ever have two sample repositories (App vs Pages Router), so it's design is going to be unique. */
function NextjsExampleRepos() {
  const { colorMode } = useColorMode();
  const isLightMode = colorMode === 'light';

  return (
    <div className="my-10">
      <div className="flex justify-center">
        <span className="translate-y-2 bg-white px-3 text-[0.8125rem]/[1.125rem] font-semibold text-[#9394A1] dark:bg-[#1A1B1E] dark:text-white">
          Example repositories
        </span>
      </div>
      <div className="flex flex-col items-center gap-4 rounded-3xl border border-gray-700/5 p-4 dark:border-white/[0.04] sm:flex-row sm:rounded-full">
        <Link
          href="https://github.com/clerk/clerk-nextjs-app-quickstart"
          target="_blank"
          className="inline-flex w-full flex-1 items-center justify-center gap-2 rounded-lg bg-[#F7F7F8] py-3 font-semibold text-white shadow-[inset_0px_0px_0px_1px_rgb(0_0_0/0.02),inset_0px_1px_0px_rgb(0_0_0/0.03)] dark:bg-[#212126] dark:shadow-[inset_0px_0px_0px_1px_rgb(255_255_255/0.02),inset_0px_1px_0px_rgb(255_255_255/0.04)] sm:rounded-full sm:py-[1.125rem]"
        >
          <Image
            src="/images/app-router-icon.svg"
            alt="Blue cube icon"
            width={22}
            height={26}
          />
          <p className="whitespace-nowrap text-xs text-[#131316] dark:text-white sm:text-[0.9375rem]/[1.125rem]">
            App <span className="text-[#9394A1]">router</span>
          </p>
        </Link>

        <Image
          src={
            isLightMode
              ? '/images/github-gradient.svg'
              : '/images/github-gradient-dark.svg'
          }
          alt="GitHub Logo with Gradient"
          width={48}
          height={48}
          className="hidden sm:block"
        />

        <Link
          href="https://github.com/clerk/clerk-nextjs-pages-quickstart"
          target="_blank"
          className="inline-flex w-full flex-1 items-center justify-center gap-2 rounded-lg bg-[#F7F7F8] py-3 font-semibold text-white shadow-[inset_0px_0px_0px_1px_rgb(0_0_0/0.02),inset_0px_1px_0px_rgb(0_0_0/0.03)] dark:bg-[#212126] dark:shadow-[inset_0px_0px_0px_1px_rgb(255_255_255/0.02),inset_0px_1px_0px_rgb(255_255_255/0.04)] sm:rounded-full sm:py-[1.125rem]"
        >
          <Image
            src="/images/pages-router-icon.svg"
            alt="Purple pages icon"
            width={21}
            height={25}
          />
          <p className="whitespace-nowrap text-xs text-[#131316] dark:text-white sm:text-[0.9375rem]/[1.125rem]">
            Pages <span className="text-[#9394A1]">router</span>
          </p>
        </Link>
      </div>
    </div>
  );
}

export function TutorialHero({
  quickstart,
  beforeYouStart,
  exampleRepo,
  children
}: TutorialHeroProps) {
  const { colorMode } = useColorMode();
  const isLightMode = colorMode === 'light';

  function capitalizeFirstLetter(string: string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  const title =
    quickstart === 'nextjs' ? 'Next.js' : capitalizeFirstLetter(quickstart);

  return (
    <>
      <Image
        src={
          isLightMode
            ? `/images/${quickstart}-clerk-logo-light.svg`
            : `/images/${quickstart}-clerk-logo-dark.svg`
        }
        alt={`${quickstart}'s logo`}
        width={228}
        height={64}
      />

      <h1 className="mt-14 text-[2rem]/10 font-semibold tracking-tight">
        Add authentication and user management to your {title} app with Clerk
      </h1>

      <div className="mt-11 gap-12 sm:flex">
        <div className="space-y-4">
          <h2 className="text-2xl font-semibold tracking-tight">
            You will learn how to:
          </h2>

          <ul className="-ml-[0.8rem] list-none space-y-1 text-[#5E5F6E] marker:text-[#D9D9DE] dark:text-[#D9D9DE] dark:marker:text-[#42434D] lg:list-disc">
            {children}
          </ul>
        </div>

        <div className="mt-11 space-y-4 sm:mt-0">
          <h2 className="text-2xl font-semibold tracking-tight">
            Before you start:
          </h2>

          <ul className="space-y-4">
            {beforeYouStart.map(item => (
              <li key={item.title}>
                <Link
                  href={item.link}
                  target="_blank"
                  className="tracking-tight text-[#6C47FF] underline underline-offset-4 dark:text-[#9785FF]"
                >
                  {item.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
      {quickstart === 'nextjs' ? (
        <NextjsExampleRepos />
      ) : (
        exampleRepo && (
          <div className="my-10">
            <div className="flex justify-center">
              <span className="translate-y-2 bg-white px-3 text-[0.8125rem]/[1.125rem] font-semibold text-[#9394A1] dark:bg-[#1A1B1E] dark:text-white">
                Example repository
              </span>
            </div>

            {exampleRepo.map(repo => (
              <div
                key={repo.title}
                className="flex flex-col items-center gap-4 rounded-3xl border border-gray-700/5 p-4 dark:border-white/[0.04] sm:flex-row sm:rounded-full"
              >
                <Link
                  href={repo.link}
                  target="_blank"
                  className="inline-flex w-full flex-1 items-center justify-center gap-2 rounded-lg bg-[#F7F7F8] py-3 font-semibold text-white shadow-[inset_0px_0px_0px_1px_rgb(0_0_0/0.02),inset_0px_1px_0px_rgb(0_0_0/0.03)] dark:bg-[#212126] dark:shadow-[inset_0px_0px_0px_1px_rgb(255_255_255/0.02),inset_0px_1px_0px_rgb(255_255_255/0.04)] sm:rounded-full sm:py-[1.125rem]"
                >
                  <Image
                    src={
                      isLightMode
                        ? '/images/github-gradient.svg'
                        : '/images/github-gradient-dark.svg'
                    }
                    alt="GitHub Logo with Gradient"
                    width={48}
                    height={48}
                    className="hidden sm:block"
                  />
                  <p className="whitespace-nowrap text-xs text-[#131316] dark:text-white sm:text-[0.9375rem]/[1.125rem]">
                    {repo.title}
                  </p>
                </Link>
              </div>
            ))}
          </div>
        )
      )}
    </>
  );
}
